import { PlainCard } from "./settings_components";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { deleteTemplate } from "../utils/firebase_operations";
import { type TemplateWithLabel } from "../utils/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../auth/AuthContext";
import { Form, Formik } from "formik";
import { object, string, ValidationError } from "yup";
import { Button, Drawer, IconButton, Tooltip } from "@mui/material";
import { getCompanies } from "../auth/operations";
import { Delete, FileCopy } from "@mui/icons-material";
import FormikSelect from "../formikComponents/formik-select";
import { transferTemplatesHandler } from "../firebase";
import { useSetAtom, useAtomValue } from "jotai";
import { customAlertStateAtom } from "./custom-alert";
import { removeItemFromArray, trackSentryException } from "../utils/helpers";
import { companyDataAtom } from "../routes/test-run/test-run-atoms";
import {
  DataGrid,
  GridActionsCellItem,
  gridClasses,
  type GridColDef,
} from "@mui/x-data-grid";
import styled from "@emotion/styled";
import { AsyncConfirmationModal } from "./asyncConfirmationModal";

function TemplatesContent() {
  const queryClient = useQueryClient();
  const { user } = useContext<any>(AuthContext);

  const { data: companyData, isLoading } = useAtomValue(
    companyDataAtom(user.companyId)
  );

  const [deleteTemplateDialogState, setDeleteTemplateDialogState] =
    useState<TemplateWithLabel | null>(null);

  const resetDeleteTemplateDialogState = useCallback(() => {
    setDeleteTemplateDialogState(null);
  }, []);

  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const deleteTemplateMutation = useMutation({
    mutationKey: ["deleteTemplate"],
    mutationFn: async () => {
      try {
        await deleteTemplate(
          companyData?.name,
          deleteTemplateDialogState!.imageUrl
        );

        await queryClient.setQueryData(
          ["getCompanyData", companyData!.organisationId],
          (oldData: any) => {
            const oldTemplates: TemplateWithLabel[] = oldData?.templates ?? [];
            const removedTemplateIndex = oldTemplates.findIndex(
              (template) =>
                template.imageUrl === deleteTemplateDialogState?.imageUrl
            );

            const newTemplates = removeItemFromArray(
              oldTemplates,
              removedTemplateIndex
            );

            return {
              ...oldData,
              templates: newTemplates,
            };
          }
        );

        setCustomAlertState({
          open: true,
          type: "success",
          title: `Template deleted successfully`,
        });
      } catch (error: any) {
        trackSentryException(error);
        console.log("Error occurred during deleting template", error);
        setCustomAlertState({
          open: true,
          type: "error",
          title: "Something went wrong",
          description: `An error occurred during deleting template`,
        });
      }
      resetDeleteTemplateDialogState();
    },
  });

  const columns: GridColDef[] = [
    {
      field: "imageUrl",
      headerName: "Template image",
      minWidth: 384,
      maxWidth: 720,
      renderCell: (params) => (
        <ImageContainer>
          <StyledImage src={params.value} alt="Template" />
        </ImageContainer>
      ),
    },
    {
      field: "label",
      headerName: "Label",
      flex: 1,
      minWidth: 128,
    },
    {
      field: "device",
      headerName: "Device",
      minWidth: 256,
    },
    {
      field: "actions",
      type: "actions",
      minWidth: 128,
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({ row }) => {
        return [
          <GridActionsCellItem
            key={"delete"}
            icon={<Delete />}
            label="Delete"
            onClick={() => {
              setDeleteTemplateDialogState(row);
            }}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <>
      <div className="relative overflow-x-auto h-full">
        <AsyncConfirmationModal
          title={"Are you sure you want to delete the template?\n"}
          description={`Template: ${deleteTemplateDialogState?.label ?? ""}`}
          actionTitle={"Delete"}
          backTitle={"Cancel"}
          closeHandler={resetDeleteTemplateDialogState}
          open={deleteTemplateDialogState != null}
          actionHandler={deleteTemplateMutation.mutateAsync}
          actionIcon={<Delete />}
          submitting={deleteTemplateMutation.isPending}
        />
        <DataGrid
          rows={companyData?.templates ?? []}
          loading={isLoading}
          showCellVerticalBorder={false}
          showColumnVerticalBorder={false}
          disableColumnMenu={true}
          className={"bg-white"}
          columns={columns}
          getRowId={(row) => row.imageUrl}
          isRowSelectable={(row) => false}
          initialState={{
            sorting: {
              sortModel: [{ field: "key", sort: "asc" }],
            },
          }}
          sx={{
            [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
              outline: "transparent",
            },
            [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
              {
                outline: "none",
              },
          }}
        />
      </div>
    </>
  );
}

function TemplatesPanel() {
  const { user } = useContext<any>(AuthContext);

  return (
    <PlainCard>
      <div className={"flex h-full"}>
        <div className={"flex-[4_1_0%]"}>
          <div className="grid grid-cols-2">
            <h1 className="text-3xl pl-4 pb-6 font-bold">Templates</h1>
          </div>
          <div className="pl-4 pr-4 overflow-auto h-[calc(100%-50.25px)]">
            <TemplatesContent />
          </div>
        </div>
        {user.role === "superUser" && <TransferTemplates />}
      </div>
    </PlainCard>
  );
}

const TransferTemplates = () => {
  const { user } = useContext<any>(AuthContext);
  const { isLoading, data, isError, error } = useQuery({
    queryKey: ["companyNames"],
    queryFn: async () => await getCompanies(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const companies = useMemo(() => {
    return data?.map((company) => company.name);
  }, [data]);

  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const [openDrawer, setOpenDrawer] = useState(false);

  const toggleDrawer = useCallback(() => {
    setOpenDrawer(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  return (
    <>
      {data != null && (
        <div className={"flex flex-col"}>
          <Tooltip title={"Transfer templates"}>
            <IconButton onClick={toggleDrawer} aria-label="delete" size="small">
              <FileCopy />
            </IconButton>
          </Tooltip>
          <Drawer open={openDrawer} anchor={"right"} onClose={closeDrawer}>
            <div className={"flex-1 flex flex-col p-8 min-w-[420px]"}>
              <p className="text-xl pl-4 pb-6 font-bold">Transfer templates</p>
              <Formik
                initialValues={{
                  sourceCompany: user.companyName,
                  targetCompany: data.at(-1).name,
                }}
                validationSchema={object()
                  .shape({
                    sourceCompany: string().required(),
                    targetCompany: string().required(),
                  })
                  .test(
                    "source-target",
                    "Source and Target companies must be different",
                    function (obj) {
                      const { sourceCompany, targetCompany } = obj;
                      if (sourceCompany === targetCompany) {
                        return new ValidationError(
                          "Source and Target companies must be different",
                          null,
                          "targetCompany"
                        );
                      }
                      return true;
                    }
                  )}
                onSubmit={async (values) => {
                  const sourceOrganisationId = data?.find(
                    (company) => company.name === values.sourceCompany
                  ).id;
                  const targetOrganisationId = data?.find(
                    (company) => company.name === values.targetCompany
                  ).id;

                  try {
                    await transferTemplatesHandler({
                      sourceOrganisationId,
                      targetOrganisationId,
                    });
                    setCustomAlertState({
                      open: true,
                      type: "success",
                      title: "Success",
                      description: "Templates transferred successfully",
                    });
                  } catch (e) {
                    trackSentryException(e);
                    setCustomAlertState({
                      open: true,
                      type: "error",
                      title: "Something went wrong",
                      description:
                        "An error occurred while transferring templates",
                    });
                  }
                  setOpenDrawer(false);
                }}
              >
                {({ isSubmitting }) => (
                  <Form className={"flex flex-col gap-5"}>
                    <FormikSelect
                      name={"sourceCompany"}
                      key={"sourceCompany"}
                      label={"Source"}
                      options={companies}
                    />
                    <FormikSelect
                      name={"targetCompany"}
                      key={"targetCompany"}
                      label={"Target"}
                      options={companies}
                    />
                    <Button
                      type={"submit"}
                      disabled={isSubmitting}
                      variant={"contained"}
                      startIcon={<FileCopy />}
                    >
                      Transfer
                    </Button>
                  </Form>
                )}
              </Formik>
            </div>
          </Drawer>
        </div>
      )}
    </>
  );
};

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-block: 8px;
  height: 100%;
`;

const StyledImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

export default TemplatesPanel;
